import { KeyboardEvent } from 'react'
import { BaseRange, Editor, Node, Path, Range, Element as SlateElement, Transforms } from 'slate'

import { BLOCK_FORMATS } from '../interface/custom-types'

import { insertNode } from './enterBehaviorHelper'

interface IIsListBeforeNestedList {
  editor: Editor
  parentPath: Path
}

interface IQuitNodeByDoubleEnter extends IIsListBeforeNestedList {
  event: KeyboardEvent
  parentNode: Node
}

const isNotLastLi = ({ editor, parentPath }: IIsListBeforeNestedList) => {
  const nextNodePath = Path.next(parentPath)
  const isNextNodeExist = Node.has(editor, nextNodePath)
  if (isNextNodeExist) {
    const nextNode = Node.get(editor, nextNodePath)
    return SlateElement.isElement(nextNode) && nextNode.type === BLOCK_FORMATS.listItem
  }

  return false
}

export const quitNodeByDoubleEnter = ({
  editor,
  event,
  parentPath,
  parentNode,
}: IQuitNodeByDoubleEnter) => {
  if (Path.hasPrevious(parentPath)) {
    const prevNodePath = Path.previous(parentPath)
    const prevNode = Node.get(editor, prevNodePath)
    const parentIsEmpty = Node.string(parentNode).trim().length === 0
    const isNeedQuit =
      parentIsEmpty &&
      SlateElement.isElement(prevNode) &&
      SlateElement.isElement(parentNode) &&
      parentNode.type === prevNode.type
    if (isNeedQuit) {
      const generalNodePath = Path.parent(parentPath)
      if (Node.has(editor, generalNodePath)) {
        const generalNode = Node.get(editor, generalNodePath)
        if (SlateElement.isElement(generalNode)) {
          const parentPathOfGeneralNode = Path.parent(generalNodePath)
          const isNestedList =
            Node.has(editor, parentPathOfGeneralNode) &&
            Node.get(editor, parentPathOfGeneralNode)['type'] === BLOCK_FORMATS.listItem

          const isCurrentListBeforeNestedList = isNotLastLi({ editor, parentPath })

          // Deletion is necessary so as not to leave an empty element if you left the parent node
          Transforms.removeNodes(editor, { at: parentPath })
          if (!isNestedList) {
            if (isCurrentListBeforeNestedList) {
              Transforms.splitNodes(editor, { at: parentPath })
            }
            insertNode(
              editor,
              {
                type: BLOCK_FORMATS.paragraph,
                children: [{ text: '' }],
              },
              generalNodePath
            )
          } else {
            if (isCurrentListBeforeNestedList) {
              Transforms.splitNodes(editor, { at: parentPath })
              Transforms.moveNodes(editor, {
                at: Path.next(generalNodePath),
                to: Path.next(Path.parent(generalNodePath)),
              })
              Transforms.wrapNodes(
                editor,
                { type: BLOCK_FORMATS.listItem, children: [] },
                { at: Path.next(Path.parent(generalNodePath)) }
              )
              insertNode(
                editor,
                {
                  type: BLOCK_FORMATS.listItem,
                  children: [{ type: BLOCK_FORMATS.paragraph, children: [{ text: '' }] }],
                },
                Path.parent(generalNodePath)
              )
            } else {
              insertNode(
                editor,
                {
                  type: BLOCK_FORMATS.listItem,
                  children: [{ type: BLOCK_FORMATS.paragraph, children: [{ text: '' }] }],
                },
                Path.parent(generalNodePath)
              )
            }
          }
          event.preventDefault()

          return true
        }
      }
    }
  }

  return false
}

export const quitFromLink = (editor: Editor, selection: BaseRange, event?: KeyboardEvent) => {
  const currentNode = Node.get(editor, selection.anchor.path)
  const currentNodeLength = Node.string(currentNode).length
  const currentNodeRange = Range.end(selection).offset
  const parentPath = Path.parent(selection.anchor.path)
  const parentNode = Node.get(editor, parentPath)

  // checking currentNodeLength and currentNodeRange will help avoid the case where we
  // press space inside the link text and instead of space we exit it
  if (parentNode['type'] === BLOCK_FORMATS.link && currentNodeLength === currentNodeRange) {
    Transforms.insertNodes(
      editor,
      {
        type: BLOCK_FORMATS.paragraph,
        children: [{ text: '' }],
      },
      {
        at: Path.next(parentPath),
      }
    )
    Transforms.select(editor, Path.next(parentPath))

    return event?.preventDefault()
  }
}
